import React, {useCallback} from "react";
import {Box, HStack, Icon, Text, VStack} from "@chakra-ui/react";

import {Pencil, Trash, ArchiveRestore} from "lucide-react";
import {useNavigate} from "react-router-dom";

import {EntityDeletionConfirmation, EntityModalContent, Button, SkeletonCircle} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useLanguage} from "@/contexts/languageContext";
import miscUtil from "@/utils/miscUtil";
import {useApi} from "@/uses/useApi";

export const EntityListBodySkeleton = ({Body, sizes, itemNbr = 15}) => {
    return <VStack
        maxH="100%"
        justify="flex-start"
        px="3vw"
        py=".5rem"
        w="100%"
        gap="3vh"
        overflowY="overlay"
        style={{position: 'relative'}}
    >
        {Array.from({length: itemNbr}).map((e, i) => {
            return <Box key={`entity-skeleton-${i}`}
                        w={sizes}
                        position="relative"
                        borderRadius="1rem"
            >
                <HStack align="stretch" gap="2rem" h="100%" p=".75rem" w="100%">
                    <Body flex={1}/>
                    <HStack gap=".75rem">
                        <SkeletonCircle>
                            <Button visual="icon">
                                <Icon as={Pencil} boxSize={5}/>
                            </Button>
                        </SkeletonCircle>
                        <SkeletonCircle>
                            <Button visual="icon">
                                <Icon as={Trash} boxSize={5}/>
                            </Button>
                        </SkeletonCircle>
                    </HStack>
                </HStack>
            </Box>
        })}
    </VStack>;
}

export const EntityListBody = ({Body, EditBody, sizes, entity, entities, setEntities, noDataText, getDeletionText = null, ...props}) => {

    const navigate = useNavigate();
    const {t} = useLanguage();
    const {open, close} = useModal();
    const api = useApi();

    const deletionText = useCallback((element) => {
        return (getDeletionText === null || miscUtil.getValueType(getDeletionText) !== 'function')
                ? <Text>{t('sentences.deletionConfirm', { object: t(`objects.${entity}`) })}</Text>
                : <Text>{t('sentences.deletionConfirm', { object: getDeletionText(element) })}</Text>
    }, []);


    const EntityBody = useCallback((e, id) => {
        return (<Box key={`entity-${id}`}
                     w={sizes}
                     position="relative"
                     borderRadius="1rem"
        >
            <HStack align="stretch" gap="2rem" h="100%" p=".75rem" w="100%">
                <Body flex={1} element={e}/>
                <HStack gap=".75rem">
                    {
                        (props?.canEdit ?? true) &&
                        <Button
                            visual="icon"
                            onClick={(_) => open(
                                <EntityModalContent
                                    title="Éditer"
                                    Body={<EditBody
                                        successCallback={(data) => {
                                            navigate(0);
                                        }}
                                        {...e}
                                    />}
                                    close={close}
                                />, 'cover')}
                        >
                            <Icon as={Pencil} boxSize={5}/>
                        </Button>
                    }
                    <Text>{  }</Text>
                    <Button
                        visual="icon"
                        onClick={(_) => {
                            if(e?.deleted === true) {
                                api.ApiService.rawPut(`/article/${e.id}/restore`)
                                    .then((res) => {
                                        if(res?.data?.type === 'success'){
                                            navigate(0);
                                        }
                                    })
                            } else {
                                open(
                                    <EntityDeletionConfirmation
                                        close={close}
                                        body={
                                          <Box p="2rem">
                                            { deletionText(e) }
                                          </Box>
                                        }
                                        entity={entity}
                                        successCallback={() => {
                                            navigate(0);
                                        }}
                                        item={e}/>
                                    , 'sm');
                            }
                        }}>
                        <Icon as={e?.deleted ? ArchiveRestore : Trash} boxSize={5}/>
                    </Button>
                    {
                        props?.customButton && React.createElement(props?.customButton, {item: e})
                    }
                </HStack>
            </HStack>
        </Box>)
    }, [setEntities, close])

    return <VStack
        maxH="100%"
        justify="flex-start"
        px="3vw"
        py=".5rem"
        w="100%"
        gap="3vh"
        overflowY="overlay"
        style={{position: 'relative'}}
    >
        {
            entities.length > 0
                ?
                entities.map((e, id) => EntityBody(e, id))
                :
                <Box style={{alignItems: 'center', justifyContent: 'center'}}>
                    <Text color="secondary.500">{noDataText}</Text>
                </Box>
        }
    </VStack>;
}

export default EntityListBody;
